import {
  type SocialPlatform,
  getSocialPlatformDetails,
} from '@realadvisor/constants';

import { type Dictionaries_Types_Enum_Enum } from '@graphql/__generated__/graphql';
import type { RaAddressType } from '@modules/_shared/components/form/RaAddressInput';
import type {
  FormFieldDefinitionType,
  FormItemGridSize,
} from '@modules/_shared/components/form/RaForm';
import { getAgentDictionaryType } from '@modules/_shared/utils/agentDictionaryFields';
import type { Translate } from '@shared/hooks/locale';

type InfoFieldConfig = {
  type: 'info';
  content: string;
  gridProps?: FormItemGridSize;
};

type AgentNameModalData = {
  first_name: string;
  last_name: string;
};

type AgentSlugModalData = {
  agent_slug: string;
};

type HiresOnRealAdvisorModalData = {
  hires_on_realadvisor: number | null;
};

type YearsInBusinessModalData = {
  active_since: number;
};

type LanguagesModalData = {
  languages: string[];
};

type ServicesModalData = {
  services: string[];
};

type CertificationsModalData = {
  certifications: string[];
};

type AboutModalData = {
  description: string;
};

type SocialMediaModalData =
  | {
      facebook_profile?: string;
      instagram_profile?: string;
      tiktok_profile?: string;
      youtube_profile?: string;
      linked_in_profile?: string;
      x_profile?: string;
    }
  | {
      facebook_url?: string;
      instagram_url?: string;
      tiktok_url?: string;
      youtube_url?: string;
      linkedin_url?: string;
      x_url?: string;
    };

type AgencyNameModalData = {
  name: string;
};

type TeamAddressModalData = RaAddressType;

type TeamWebsiteModalData = {
  website_url?: string;
};

type TeamPhoneModalData = {
  phone_number?: string;
};

export type ModalDataMap = {
  agentName: AgentNameModalData;
  agentSlug: AgentSlugModalData;
  agencyName: AgencyNameModalData;
  hiresOnRealAdvisor: HiresOnRealAdvisorModalData;
  yearsInBusiness: YearsInBusinessModalData;
  languages: LanguagesModalData;
  services: ServicesModalData;
  certifications: CertificationsModalData;
  about: AboutModalData;
  socialMedia: SocialMediaModalData;
  teamAddress: TeamAddressModalData;
  teamWebsite: TeamWebsiteModalData;
  teamPhone: TeamPhoneModalData;
};

type ModalConfig<T extends Record<string, any>> = {
  title: string;
  fields: readonly (FormFieldDefinitionType<T> | InfoFieldConfig)[];
};

type EditModalConfigs = {
  [K in keyof ModalDataMap]: ModalConfig<ModalDataMap[K]>;
};

export const SOCIAL_PLATFORMS = (
  [
    'facebook',
    'instagram',
    'tiktok',
    'youtube',
    'linkedin',
    'x',
  ] as const satisfies readonly SocialPlatform[]
).map(key => ({
  key,
  label: getSocialPlatformDetails(key).label,
}));

export const getSocialMediaFieldName = (
  key: SocialPlatform,
  isTeam: boolean,
) => {
  const suffix = isTeam ? '_url' : '_profile';
  const prefix = key === 'linkedin' && !isTeam ? 'linked_in' : key;
  return `${prefix}${suffix}`;
};

export const getActiveSocialPlatforms = (
  obj: object,
  isTeam: boolean,
): SocialPlatform[] =>
  SOCIAL_PLATFORMS.filter(({ key }) => {
    const field = getSocialMediaFieldName(key, isTeam);
    const value = (obj as Record<string, unknown>)[field];
    return typeof value === 'string' && value.trim().length > 0;
  }).map(({ key }) => key);

export const createSocialMediaFields = (
  isTeam: boolean,
  gridProps: FormItemGridSize = { xs: 12 },
) =>
  SOCIAL_PLATFORMS.map(({ key, label }) => ({
    type: 'text' as const,
    name: getSocialMediaFieldName(key, isTeam) as any,
    label,
    gridProps,
  }));

const createDictionaryField = <T extends string>(
  name: T,
  label: string,
  dictionaryType: Dictionaries_Types_Enum_Enum,
) => ({
  type: 'dictionary' as const,
  name,
  label,
  dictionaryType,
  multiple: true,
  gridProps: { md: 12 },
});

export const EDIT_MODAL_CONFIGS = (
  t: Translate,
  isTeam: boolean,
  isAdmin: boolean = false,
): EditModalConfigs => {
  const configs: EditModalConfigs = {
    agentName: {
      title: t('Name'),
      fields: [
        {
          type: 'text',
          name: 'first_name',
          label: t('First name'),
          required: true,
          gridProps: { md: 6 },
        },
        {
          type: 'text',
          name: 'last_name',
          label: t('Last name'),
          required: true,
          gridProps: { md: 6 },
        },
      ],
    },

    agentSlug: {
      title: t('Profile URL'),
      fields: [
        {
          type: 'info',
          content: t(
            'Used to build the public profile URL. Use lowercase letters, numbers, and hyphens only.',
          ),
          gridProps: { md: 12 },
        },
        {
          type: 'text',
          name: 'agent_slug',
          label: t('Slug'),
          gridProps: { md: 12 },
        },
      ],
    },

    hiresOnRealAdvisor: {
      title: t('Hires on RealAdvisor'),
      fields: isAdmin
        ? [
            {
              type: 'info',
              content: t(
                'Number of mandates the agent has signed with contacts that found them through RealAdvisor.',
              ),
              gridProps: { md: 12 },
            },
            {
              type: 'number',
              name: 'hires_on_realadvisor',
              label: t('Number of hires'),
              gridProps: { md: 12 },
              min: 0,
              disableFormatting: true,
            },
          ]
        : [
            {
              type: 'info',
              content: t(
                'This field indicates how many mandates you have signed with contacts that found you through RealAdvisor.\n\nSources of contacts can include:\n• Valuation requests made after an appraisal on RealAdvisor\n• Filling out the contact form on your profile\n• Direct phone calls\n• Agency walk-ins\n\nPlease **contact your customer success manager** for more information on how to update this field.',
              ),
              gridProps: { md: 12 },
            },
          ],
    },

    yearsInBusiness: {
      title: t('Years in business'),
      fields: [
        {
          type: 'number',
          name: 'active_since',
          label: t('Active since (year)'),
          gridProps: { md: 12 },
          min: 1900,
          max: new Date().getUTCFullYear(),
          disableFormatting: true,
        },
      ],
    },

    languages: {
      title: t('Languages spoken'),
      fields: [
        createDictionaryField(
          'languages',
          t('Languages'),
          getAgentDictionaryType('languages'),
        ),
      ],
    },

    services: {
      title: t('Services'),
      fields: [
        createDictionaryField(
          'services',
          t('Services'),
          getAgentDictionaryType('services'),
        ),
      ],
    },

    certifications: {
      title: t('Certifications'),
      fields: [
        createDictionaryField(
          'certifications',
          t('Certifications'),
          getAgentDictionaryType('certifications'),
        ),
      ],
    },

    about: {
      title: t('About'),
      fields: [
        {
          type: 'text',
          name: 'description',
          label: t('Description'),
          multiline: true,
          rows: 6,
          gridProps: { md: 12 },
        },
      ],
    },

    socialMedia: {
      title: t('Social media links'),
      fields: createSocialMediaFields(isTeam),
    },

    agencyName: {
      title: t('Name'),
      fields: [
        {
          type: 'text',
          name: 'name',
          label: t('Name'),
          required: true,
          gridProps: { md: 12 },
        },
      ],
    },

    teamAddress: {
      title: t('Address'),
      fields: [
        {
          type: 'address',
          label: t('Address'),
          gridProps: { xs: 12 },
        },
      ],
    },

    teamWebsite: {
      title: t('Website URL'),
      fields: [
        {
          type: 'text',
          name: 'website_url',
          label: t('Website URL'),
          gridProps: { md: 12 },
        },
      ],
    },

    teamPhone: {
      title: t('Phone'),
      fields: [
        {
          type: 'phone_number',
          name: 'phone_number',
          label: t('Phone number'),
          gridProps: { md: 12 },
        },
      ],
    },
  };

  return configs;
};

export type GenericEditModalType = keyof ModalDataMap;
